import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { format } from 'date-fns';
import queryString from 'query-string';
import {
  ButtonSecondary,
  ButtonSize,
  formatBalance,
  IconDownload,
  IconLeft,
  IconLoader,
  IconRight,
  PermissionEnum,
  SelectItemProps,
  // usePrevious,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { setNotificationVisible } from '../../../../../store/slice';
import {
  setFilteredWarningsReportTotal,
  setSelectedOrganizations,
} from './store/slice';
import WarningItemRows from '../../components/warnings-report/warning-item-rows';
import WarningsReportFilters from '../../components/warnings-report/filters';
import {
  useLazyGetWarningsReportQuery,
  useLazyGetOrganizationsByIdsQuery,
  useExportWarningReportMutation,
  useLazyGetMonitoringRulesWarningsReportQuery,
} from './store/service';
import useGetAutocompleteList from './hooks/use-get-organizations';
import WarningItemRow from '../../components/warnings-report/warning-item-row';

import cn from './warning-report.module.css';

import ROUTES from '../../routes';

const WarningsReportOverview = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [maxCount, setMaxCount] = useState(0);
  const [filters, setFilters] = useState<{
    date?: string[];
    organizationId?: string[];
    organizationType?: string[];
    transactionTypeId?: string[];
    transactionPaymentType?: string[];
    paymentOrderType?: string[];
    actions?: string[];
  }>({
    date: undefined,
    organizationId: undefined,
    organizationType: undefined,
    transactionTypeId: undefined,
    transactionPaymentType: undefined,
    paymentOrderType: undefined,
    actions: undefined,
  });
  const [showDetalisation, setShowDetalisation] = useState(false);
  const [queryStringValue, setQuery] = useState('');
  const [maxMonitoringRulesCount, setMaxMonitoringRulseCount] = useState(0);
  const [
    nextTransactionMonitroingRulesIsLoading,
    setNexTransactionMonitoringRulesIsLoading,
  ] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const [getWarningsReport, { isLoading, isFetching }] =
    useLazyGetWarningsReportQuery();
  const [getOrganizationsByIds] = useLazyGetOrganizationsByIdsQuery();
  const [
    getMonitoringRulesWarningsReport,
    {
      isLoading: monitoringRulesWarningsReportIsLoading,
      isFetching: monitoringRulesWarningsReportIsFetching,
    },
  ] = useLazyGetMonitoringRulesWarningsReportQuery();

  const [
    exportWarningReport,
    { isSuccess: exportIsSuccess, isLoading: exportIsLoading },
  ] = useExportWarningReportMutation();

  const {
    warningsReport: {
      selectedTotalWarnings,
      paymentTypes,
      transactionTypes,
      selectedOrganizations,
      warningsNumbersOfAlerts,
      monitoringRulesWarningsReports,
    },
    reportsDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const onExportClick = async () => {
    const exportFilters: Record<string, string[] | string> = {};

    if (filters && Object.keys(filters)?.length) {
      Object.keys(filters)?.forEach((item) => {
        if (item !== 'date') {
          if (
            item === 'organizationId' ||
            item === 'organizationType' ||
            item === 'transactionTypeId' ||
            item === 'transactionPaymentType' ||
            item === 'paymentOrderType'
          ) {
            exportFilters[item] = filters[item] as string[];
          }
        } else if (item === 'date') {
          const createdAt = filters[item];

          if (createdAt) {
            const [from, to] = createdAt;

            exportFilters.createdAtFrom = from;

            if (to) {
              exportFilters.createdAtTo = to;
            }
          }
        }
      });
    }

    await exportWarningReport(exportFilters);
  };

  const {
    autocompleteList,
    onSearch,
    organizationIsLoading,
    setOrganizationSearchText,
    organizationSearchText,
    wrapperRef,
    onScroll,
    nextPageIsLoading,
  } = useGetAutocompleteList();

  const onFilter = (filtersValues: {
    date?: string[];
    organizationId?: string[];
    organizationType?: string[];
    transactionTypeId?: string[];
    transactionPaymentType?: string[];
    paymentOrderType?: string[];
    actions?: string[];
  }) => {
    const nextFiltersValues: {
      date?: string[];
      organizationId?: string[];
      organizationType?: string[];
      transactionTypeId?: string[];
      transactionPaymentType?: string[];
      paymentOrderType?: string[];
      createdAtFrom?: string;
      createdAtTo?: string;
      actions?: string[];
    } = { ...filtersValues };

    if (filtersValues.date?.length) {
      const [from, to] = filtersValues.date;

      nextFiltersValues.createdAtFrom = from;

      if (to) {
        nextFiltersValues.createdAtTo = to;
      }
    }

    setFilters(nextFiltersValues);

    const queryStringFilters = { ...nextFiltersValues };

    delete queryStringFilters.date;

    let queryObject = { ...queryStringFilters };

    if (queryObject?.createdAtFrom) {
      queryObject = {
        ...queryObject,
        createdAtFrom: format(
          new Date(queryObject?.createdAtFrom),
          'yyyy-MM-dd',
        ),
      };
    }

    if (queryObject?.createdAtTo) {
      queryObject = {
        ...queryObject,
        createdAtTo: format(new Date(queryObject?.createdAtTo), 'yyyy-MM-dd'),
      };
    }

    const queryValue = `?${queryString.stringify(queryObject)}`;

    navigate(queryValue);

    if (
      Object.keys(queryStringFilters)?.filter(
        // @ts-ignore
        (item) => queryStringFilters[item],
      )?.length
    ) {
      getWarningsReport(`?${queryString.stringify(queryStringFilters)}`);

      setQuery(queryValue);
    } else {
      setQuery('');
    }
  };

  const getOrganizationsByIdsAction = useCallback(
    async (ids: string[], selectedListValues: SelectItemProps[]) => {
      const idsQuery = `&ids=${ids?.join(`&ids=`)}`;

      const response = await getOrganizationsByIds(idsQuery);

      if (response?.data?.data) {
        const nextList = ([] as SelectItemProps[]).concat(selectedListValues);

        response?.data?.data?.edges?.forEach((item) =>
          nextList.push({
            name:
              item.node?.type === 'CORPORATE'
                ? (item.node.companyName as string)
                : `${item.node.firstName} ${item.node.lastName}`,
            value: item.node.id,
          }),
        );

        dispatch(setSelectedOrganizations(nextList));
      }
    },
    [],
  );

  const onMonitoringRulesScroll = async (e: any) => {
    const { scrollTop } = e.target;

    if (scrollRef && scrollRef.current) {
      const { scrollHeight, clientHeight } = scrollRef.current;

      if (
        monitoringRulesWarningsReports?.monitoringsRulesTotals.pageInfo
          ?.hasNextPage &&
        scrollTop >= scrollHeight - clientHeight - 20
      ) {
        setNexTransactionMonitoringRulesIsLoading(true);

        const response = await getMonitoringRulesWarningsReport(
          `?take=20&cursor=${
            monitoringRulesWarningsReports?.monitoringsRulesTotals.pageInfo
              .endCursor
          }${
            search
              ? search
                  ?.split('&')
                  ?.filter((item) => !item.includes('selectedTypeId'))
                  ?.join('&')
                  ?.replace('?', '&')
              : ''
          }`,
        );

        if (response) {
          setNexTransactionMonitoringRulesIsLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    if (!search && !warningsNumbersOfAlerts) {
      getWarningsReport('');
    }
  }, [search, warningsNumbersOfAlerts]);

  useEffect(() => {
    if (warningsNumbersOfAlerts && warningsNumbersOfAlerts?.totals) {
      const counts = Object.keys(warningsNumbersOfAlerts.totals)?.map(
        // @ts-ignore
        (item) => warningsNumbersOfAlerts.totals[item],
      );

      setMaxCount(Math.max(...counts));
    }

    if (warningsNumbersOfAlerts && warningsNumbersOfAlerts?.filteredTotals) {
      dispatch(
        setFilteredWarningsReportTotal(
          warningsNumbersOfAlerts?.filteredTotals?.total,
        ),
      );
    }
  }, [warningsNumbersOfAlerts]);

  useEffect(() => {
    if (showDetalisation && monitoringRulesWarningsReports) {
      const counts =
        monitoringRulesWarningsReports?.monitoringsRulesTotals?.monitoringsRules?.map(
          (item) => item.warningsTotal,
        );

      setMaxMonitoringRulseCount(Math.max(...counts));
    }
  }, [showDetalisation, monitoringRulesWarningsReports]);

  useEffect(() => {
    if (search && !warningsNumbersOfAlerts) {
      const parsed = queryString.parse(search);

      const nextFilters: {
        date?: string[];
        organizationId?: string[];
        organizationType?: string[];
        transactionTypeId?: string[];
        transactionPaymentType?: string[];
        paymentOrderType?: string[];
        actions?: string[];
      } = {};

      if (parsed) {
        if (parsed?.selectedTypeId) {
          setShowDetalisation(true);
        }

        if (parsed?.createdAtFrom) {
          const dateFilter: string[] = [];

          dateFilter.push(
            new Date(parsed?.createdAtFrom as string).toISOString(),
          );

          if (parsed?.createdAtTo) {
            dateFilter.push(
              new Date(parsed?.createdAtTo as string).toISOString(),
            );
          }

          nextFilters.date = dateFilter;
        }

        if (parsed?.organizationId) {
          nextFilters.organizationId = Array.isArray(parsed?.organizationId)
            ? (parsed?.organizationId as string[])
            : [parsed.organizationId];
        }

        if (parsed?.actions) {
          nextFilters.actions = Array.isArray(parsed?.actions)
            ? (parsed?.actions as string[])
            : [parsed.actions];
        }

        if (parsed?.organizationType) {
          nextFilters.organizationType = Array.isArray(parsed?.organizationType)
            ? (parsed?.organizationType as string[])
            : [parsed.organizationType];
        }

        if (parsed?.transactionTypeId) {
          nextFilters.transactionTypeId = Array.isArray(
            parsed?.transactionTypeId,
          )
            ? (parsed?.transactionTypeId as string[])
            : [parsed.transactionTypeId];
        }

        if (parsed?.transactionPaymentType) {
          nextFilters.transactionPaymentType = Array.isArray(
            parsed?.transactionPaymentType,
          )
            ? (parsed?.transactionPaymentType as string[])
            : [parsed.transactionPaymentType];
        }

        if (parsed?.paymentOrderType) {
          nextFilters.paymentOrderType = Array.isArray(parsed?.paymentOrderType)
            ? (parsed?.paymentOrderType as string[])
            : [parsed.paymentOrderType];
        }

        setFilters(nextFilters);

        if (!parsed?.selectedTypeId) {
          getWarningsReport(
            search
              ?.split('&')
              ?.filter((item) => !item.includes('selectedTypeId'))
              ?.join('&'),
          );
        }

        setQuery(search);
      }
    }
  }, [search, autocompleteList, warningsNumbersOfAlerts]);

  useEffect(() => {
    if (autocompleteList?.length && filters && filters.organizationId) {
      if (filters?.organizationId?.length) {
        const unnamedOrganizations: string[] = [];

        filters.organizationId?.forEach((item) => {
          if (!autocompleteList?.find((listItem) => listItem.value === item)) {
            unnamedOrganizations.push(item);
          }
        });

        if (!selectedOrganizations?.length) {
          const nextList = filters.organizationId?.filter((item) =>
            unnamedOrganizations?.every((unnamed) => item !== unnamed),
          );

          dispatch(
            setSelectedOrganizations(
              nextList?.map((item) => ({
                name: autocompleteList?.find(
                  (listItem) => listItem.value === item,
                )?.name as string,
                value: item,
              })),
            ),
          );

          if (unnamedOrganizations?.length) {
            getOrganizationsByIdsAction(
              unnamedOrganizations,
              nextList?.map((item) => ({
                name: autocompleteList?.find(
                  (listItem) => listItem.value === item,
                )?.name as string,
                value: item,
              })),
            );
          }
        }
      } else {
        const nextFilters = { ...filters };

        nextFilters.organizationId = undefined;

        setFilters(nextFilters);
      }
    }
  }, [autocompleteList, filters, selectedOrganizations]);

  useEffect(() => {
    if (exportIsSuccess) {
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'warnings-report',
        }),
      );
    }
  }, [exportIsSuccess]);

  useEffect(() => {
    if (showDetalisation) {
      getMonitoringRulesWarningsReport(
        `?take=20${
          search
            ? search
                ?.split('&')
                ?.filter((item) => !item.includes('selectedTypeId'))
                ?.join('&')
                ?.replace('?', '&')
            : ''
        }`,
      );
    }
  }, [showDetalisation, search]);

  return (
    <div className={cn.warningsReportOverview}>
      <div className={cn.warningsReportOverviewControls}>
        <WarningsReportFilters
          filters={filters}
          onFilter={onFilter}
          onSearch={onSearch}
          autocompleteList={autocompleteList}
          selectedOrganizations={selectedOrganizations}
          organizationIsLoading={organizationIsLoading}
          organizationSearchText={organizationSearchText}
          setOrganizationSearchText={setOrganizationSearchText}
          wrapperRef={wrapperRef}
          onScroll={onScroll}
          nextPageIsLoading={nextPageIsLoading}
          transactionTypes={transactionTypes}
          paymentTypes={paymentTypes}
        />
      </div>
      <div className={cn.warningsReportOverviewContent} ref={ref}>
        <div className={cn.warningsReportOverviewContentTop}>
          <div className={cn.warningsReportOverviewContentTopLeft}>
            {showDetalisation && (
              <>
                <button
                  type="button"
                  className={cn.hideDetalisation}
                  onClick={() => {
                    setShowDetalisation(false);
                    setFilters({ ...filters });

                    const nextQuery = search
                      ?.split('&')
                      ?.filter((item) => !item.includes('selectedTypeId'))
                      ?.join('&');

                    navigate(nextQuery);
                  }}
                >
                  <IconLeft />
                </button>
                <div className={cn.detalisationTitle}>
                  Transactions monitoring rules:
                </div>
              </>
            )}
            <div className={cn.warningsReportOverviewContentTopLeftValue}>
              <div
                className={`${cn.count} ${
                  showDetalisation ? cn.lessFontSize : ''
                }`}
              >
                {/* eslint-disable-next-line no-nested-ternary */}
                {isLoading || monitoringRulesWarningsReportIsLoading ? (
                  <IconLoader width={14} height={14} />
                ) : showDetalisation ? (
                  formatBalance(
                    (monitoringRulesWarningsReports?.total as number) || 0,
                    undefined,
                    0,
                  )
                ) : (
                  formatBalance(
                    (warningsNumbersOfAlerts?.totals?.total as number) || 0,
                    undefined,
                    0,
                  )
                )}
              </div>
              <div className={cn.text}>Total warnings</div>
            </div>
            {selectedTotalWarnings?.toString() &&
              Object.keys(filters)?.filter(
                // @ts-ignore
                (item) => filters[item],
              )?.length > 0 && (
                <>
                  <div className={cn.separator}>/</div>
                  <div className={cn.warningsReportOverviewContentTopLeftValue}>
                    <div
                      className={`${cn.count} ${
                        showDetalisation ? cn.lessFontSize : ''
                      }`}
                    >
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {isFetching || monitoringRulesWarningsReportIsFetching ? (
                        <IconLoader width={14} height={14} />
                      ) : showDetalisation ? (
                        formatBalance(
                          (monitoringRulesWarningsReports?.filteredTotal as number) ||
                            0,
                          undefined,
                          0,
                        )
                      ) : (
                        formatBalance(selectedTotalWarnings || 0, undefined, 0)
                      )}
                    </div>
                    <div className={cn.text}>Selected</div>
                  </div>
                </>
              )}
            {Object.keys(filters)?.filter(
              // @ts-ignore
              (item) => filters[item],
            )?.length > 0 && (
              <NavLink
                to={`${ROUTES.WARNING_REPORT_LIST}${queryStringValue}${
                  showDetalisation
                    ? `${queryStringValue?.length ? '&' : '?'}type=MONITORING`
                    : ''
                }`}
                target="_blank"
                className={cn.listLink}
              >
                Show in list <IconRight />
              </NavLink>
            )}
          </div>
          {activePermissions?.includes(
            PermissionEnum.WARNINGS_DOWNLOAD_ACTION,
          ) &&
            !showDetalisation && (
              <ButtonSecondary
                text={exportIsLoading ? 'Loading...' : 'Download Report'}
                icon={<IconDownload />}
                className="button-white"
                size={ButtonSize.SMALL}
                onClick={onExportClick}
              />
            )}
        </div>
        <div
          className={cn.warningsReportOverviewContentBottom}
          ref={scrollRef}
          onScroll={(e) => onMonitoringRulesScroll(e)}
        >
          {(isLoading || isFetching) && (
            <div className={cn.graphLoader}>
              <IconLoader />
            </div>
          )}
          {!showDetalisation && (
            <WarningItemRows
              setShowDetalisation={setShowDetalisation}
              maxCount={maxCount}
              warningsNumbersOfAlerts={warningsNumbersOfAlerts}
              filtersLength={
                Object.keys(filters)?.filter(
                  // @ts-ignore
                  (item) => filters[item],
                )?.length
              }
              isLoading={isLoading || isFetching}
            />
          )}
          {showDetalisation && (
            <>
              {(monitoringRulesWarningsReportIsLoading ||
                (monitoringRulesWarningsReportIsFetching &&
                  !monitoringRulesWarningsReports)) && (
                <div className={cn.monitoringRulesWarningsLoader}>
                  <IconLoader />
                </div>
              )}
              {monitoringRulesWarningsReportIsFetching &&
                !monitoringRulesWarningsReportIsLoading &&
                !nextTransactionMonitroingRulesIsLoading && (
                  <div className={cn.graphLoader}>
                    <IconLoader />
                  </div>
                )}
              {monitoringRulesWarningsReports &&
                monitoringRulesWarningsReports?.monitoringsRulesTotals?.monitoringsRules?.map(
                  (item) => (
                    <WarningItemRow
                      key={item.node.id}
                      title={item.node.name}
                      warningItem={item.warningsTotal}
                      maxCount={maxMonitoringRulesCount}
                      filteredWarningItem={
                        Object.keys(filters)?.filter(
                          // @ts-ignore
                          (filterItem) => filters[filterItem],
                        )?.length > 0 &&
                        !monitoringRulesWarningsReportIsLoading &&
                        !monitoringRulesWarningsReportIsFetching
                          ? item.filteredWarningsTotal
                          : undefined
                      }
                      monitoringRuleId={item.node.id}
                      monitoringRules
                    />
                  ),
                )}
              {nextTransactionMonitroingRulesIsLoading && (
                <div className={cn.nextPageLoader}>
                  <IconLoader />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WarningsReportOverview;
